import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Avatar,
  Grid,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import RegularizationForm from "./RegularizationForm";
import regularizationEmptyStateSvg from "../../assets/emptystate_regularisation.svg";
import {
  useApplyRegularizationMutation,
  useFetchRegularizationStatusMutation,
  useGetAssociatedManagerQuery,
} from "../../api/regularizationApi";
import { setRegularization } from "../../store/reducers/regularizationSlice";
import { useDispatch, useSelector } from "react-redux";
import { useGetEmployeesListQuery } from "../../api/notificationApi";
import CCDropdown from "./CCDropdown";
import { getToken } from "../../services/Token";
import { toast } from "react-toastify";

const RegularizationPanel = ({
  regularizations,
  onRemoveRegularization,
  setRegularizations,
}) => {
  const hasRegularizations = regularizations?.length > 0;
  const [applyRegularization] = useApplyRegularizationMutation();
  const [fetchRegularizationStatus] = useFetchRegularizationStatusMutation();
  const [isSubmitDisable, setIsSubmitDisable] = useState(false);
  const dispatch = useDispatch();
  const { regularization, regularizationEmployeeId } = useSelector((state) => state.regularization);
  useGetEmployeesListQuery();
  const role = getToken("roles");
  const isAdmin = role ? role.includes("ADMIN") : false;
  const employeeId = isAdmin ? regularizationEmployeeId : getToken("Id");
  const appliedBY = getToken("Id")
  const { currentData } = useGetAssociatedManagerQuery(employeeId, {
    skip: !employeeId,
  });

  const handleSubmit = async () => {
    setIsSubmitDisable(true)
    const payload = {
      employeeId: employeeId,
      appliedBy: appliedBY,
      appliedTo: currentData?.data?.id,
      approvedBy: "",
      rejectedBy: "",
      ccList: regularization?.cc?.map((item) => item.employeeId),
      remarks: regularization?.remarks,
      entries: regularizations?.map((item) => ({
        date: item.formattedDate,
        reason: item.reason || "",
        inTime: {
          hour: parseInt(item.fromTime.split(":")[0], 10) || 0,
          minute: parseInt(item.fromTime.split(":")[1], 10) || 0,
        },
        outTime: {
          hour: parseInt(item.toTime.split(":")[0], 10) || 0,
          minute: parseInt(item.toTime.split(":")[1], 10) || 0,
        },
      })),
    };

    try {
      await applyRegularization(payload).unwrap();
      setRegularizations([]);
      dispatch(setRegularization({}));
      const startDate = getToken('fromDate')
      const endDate = getToken('toDate')
      const body = {
        employeeId: employeeId,
        fromDate: startDate,
        toDate: endDate,
      };
      fetchRegularizationStatus(body);
      toast.success("Regularization applied successfully");
      setIsSubmitDisable(false)
    } catch (e) {
      toast.error("Failed to apply for regularization");
      console.log(e);
      setIsSubmitDisable(false)
    }
  };

  if (!hasRegularizations) {
    return (
      <Paper sx={{ flex: 2, p: 3 }}>
        <div className="flex items-center justify-center flex-col h-full">
          <img src={regularizationEmptyStateSvg} alt="regularization" />
          <p className="mt-3 text-gray-600">
            To apply for regularization, kindly choose a date from the calendar.
          </p>
        </div>
      </Paper>
    );
  }

  return (
    <Paper sx={{ flex: 2, p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Avatar sx={{ width: 40, height: 40, backgroundColor: "#666" }}>
          <Person />
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
            {currentData?.data?.name?.split("(")[0]}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            # {currentData?.data?.name?.match(/\(([^)]+)\)/)?.[1]}
          </Typography>
        </Box>
      </Box>

      <Grid item xs={4} className="!mb-4">
        <TextField
          onChange={(e) => {
            const newReg = { ...regularization, remarks: e.target.value };
            dispatch(setRegularization(newReg));
          }}
          size="small"
          value={regularization?.remarks}
          fullWidth
          InputProps={{ style: { fontSize: "14px" } }}
          placeholder="Remarks"
        />
      </Grid>

      <div className="flex gap-3 items-center mb-3">
        <CCDropdown />
      </div>

      <RegularizationForm
        regularizations={regularizations}
        removeRegularization={onRemoveRegularization}
        setRegularizations={setRegularizations}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: "#ddd",
            color: "#666",
            "&:hover": {
              borderColor: "#bbb",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitDisable}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: "#2196F3",
            "&:hover": {
              backgroundColor: "#1976D2",
            },
          }}
        >
          Submit
        </Button>
      </Box>
    </Paper>
  );
};

export default RegularizationPanel;
